import { Base } from '@studiometa/js-toolkit';
import { removeClass, toggleClass } from '@studiometa/js-toolkit/utils';
import Menu from './Menu.js';
import Search from '../Search/index.js';
import { Action } from '@studiometa/ui';

/**
 * Header component
 */
export default class Header extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'Header',
    components: {
      Menu,
      Action,
      Search,
    },
  };

  /**
   *
   */
  mounted() {
    this.setMobileHeight();
  }

  /**
   * Keep the header height up to date, the search overlay depends on it.
   */
  resized() {
    this.setMobileHeight();
  }

  /**
   *
   */
  setMobileHeight() {
    document.documentElement.style.setProperty(
      '--mobile-menu-height',
      `${this.$el.offsetHeight}px`,
    );
  }

  /**
   * Set height
   * @param {HTMLElement} currentMenu The current menu
   */
  setHeight(currentMenu) {
    const menuList = currentMenu.querySelector('.menu-list');
    if (menuList) {
      const menuHeight = menuList.offsetHeight;

      document.documentElement.style.setProperty(
        '--menu-height',
        `${this.$el.offsetHeight + menuHeight}px`,
      );
    }
  }

  /**
   * Service scrolled hook
   * @see https://js-toolkit.studiometa.dev/api/methods-hooks-services.html#scrolled
   * @param {object} props Props
   * @param {number} props.y The current vertical scroll position.
   * @param {object} props.direction For both axis, whether or not the current position has changed since the last one.
   */
  scrolled({ y, direction }) {
    const isOnTop = y <= 0;
    const isNearTop = y < 128;

    toggleClass(document.documentElement, 'is-on-top', isOnTop);
    toggleClass(document.documentElement, 'is-near-top', isNearTop);

    if (direction.y === 'NONE') {
      return;
    }

    toggleClass(document.documentElement, 'is-scrolling-down', direction.y === 'DOWN');
    toggleClass(document.documentElement, 'is-scrolling-up', direction.y === 'UP');
    toggleClass(this.$el, 'header--solid', direction.y === 'UP');

    if (isNearTop) {
      removeClass(this.$el, 'header--solid');
    }
  }

  /**
   * Whether at least one menu of the header is open.
   * @returns {boolean}
   */
  get hasOpenMenu() {
    return this.$query('Menu').some(({ isOpen }) => isOpen);
  }

  /**
   * @param {object} props Props
   * @param {{ target: MenuList }} props.event Event
   */
  onMenuOpen({ event }) {
    toggleClass(document.documentElement, 'is-menu-open', this.hasOpenMenu);
    this.setHeight(event?.target);
    this.closeSearch();
  }

  /**
   * A desktop submenu opens on hover.
   */
  onMenuSubmenuOpen() {
    this.closeSearch();
  }

  /**
   * Close the search when a menu opens.
   */
  closeSearch() {
    for (const search of this.$children.Search) {
      search.close({ restoreFocus: false });
    }
  }

  /**
   *
   */
  onMenuClose() {
    toggleClass(document.documentElement, 'is-menu-open', this.hasOpenMenu);
  }

  /**
   * Close the menus when the search opens, it can be opened from the mobile menu.
   */
  onSearchOpen() {
    for (const menu of this.$children.Menu) {
      menu.close();
    }
  }
}
